import { TFunction } from 'i18next'

import enTranslations from 'app/i18n/locales/en.json'
import { isArchivedV2 } from 'app/models/Organization'
import { PlanWithServices } from 'app/models/scribe.models'
import { colors } from 'app/theme'

export const FR_TO_EN_COLUMNS: { [id: string]: string } = {
  identifiant_unique: 'unique_identifier',
  prenom: 'first_name',
  nom_de_famille: 'last_name',
  date_de_naissance: 'date_of_birth',
  courriel_de_communication: 'communication_email',
  forfait: 'plan',
  date_d_activation: 'activation_date',
  date_de_desactivation: 'deactivation_date',
}
export const EN_TO_FR_COLUMNS = Object.fromEntries(
  Object.entries(FR_TO_EN_COLUMNS).map((a) => a.reverse()),
)

export const translate = (target: string, dictionary: { [frKey: string]: string }) => {
  const header = target.trim().toLowerCase()
  return header in dictionary ? dictionary[header] : header
}

export const getSnowplowSchema = (name: string, version: string) => {
  return `iglu:co.dialogue/${name}/jsonschema/${version}`
}

export const getPlansThatContainOption = (
  plans: PlanWithServices[],
  requestedOption: string,
): PlanWithServices[] => {
  const activePlans = plans.filter((plan) => !isArchivedV2(plan))

  return activePlans.filter((plan: PlanWithServices) =>
    Object.values(plan.services).some((services) =>
      Object.values(services).some(
        (options) => options[requestedOption] && options[requestedOption] !== 'disabled',
      ),
    ),
  )
}

export const getPlansThatContainService = (
  plans: PlanWithServices[],
  requestedService: string,
): PlanWithServices[] => {
  const activePlans = plans.filter((plan) => !isArchivedV2(plan))

  return activePlans.filter((plan: PlanWithServices) =>
    Object.values(plan.services).some((services) =>
      Object.keys(services).some((service) => service === requestedService),
    ),
  )
}

export const minOnePlanHasOption = (
  plans: PlanWithServices[],
  requestedOption: string,
): boolean => {
  const filteredPlans = getPlansThatContainOption(plans, requestedOption)

  return filteredPlans.length > 0
}

export const minOnePlanHasService = (
  plans: PlanWithServices[],
  requestedService: string,
): boolean => {
  const filteredPlans = getPlansThatContainService(plans, requestedService)

  return filteredPlans.length > 0
}

export const getPlansIdToLabel = (plans: PlanWithServices[]) =>
  plans.reduce((acc, plan) => ({ ...acc, [plan.id]: plan.label }), {} as { [id: string]: string })

export const getProvinceName = (isoCode: string, name: string, t: TFunction): string => {
  if (Object.keys(enTranslations.supportedProvinces).includes(isoCode)) {
    return t(`supportedProvinces.${isoCode}`, { defaultValue: '' }) as string
  }
  if (!name) return isoCode
  return `${name} (${isoCode})`
}

export const getSortedArrayPerColumn = (
  items: { label: string; value: string }[],
  colsCount: number,
): { label: string; value: string }[] => {
  if (colsCount < 2) return items
  const itemsCount = items.length
  if (itemsCount < colsCount) return items

  const rowsCount = Math.ceil(itemsCount / colsCount)
  const fullCols = itemsCount % colsCount

  const newItems = [...Array(itemsCount)]

  for (let i = 0, index = 0; i < rowsCount; i++) {
    for (let j = 0; j < colsCount; j++) {
      let newIndex = i + j * rowsCount
      if (fullCols && j >= fullCols + 1) {
        --newIndex
      }
      newItems[index] = items[newIndex]
      index++
      if (index >= itemsCount) break
    }
  }

  return newItems
}

/**
 * Determines the appropriate text color (black or white) for a given background color
 * based on its brightness.
 *
 * **Weights** (299, 587, 114): These values are derived from the luminance formula used in color science, which accounts for the human eye's sensitivity to different colors.
 *
 * **Threshold** (128): This is a common threshold used to determine whether a color is light or dark. It ensures that the text color chosen will have sufficient contrast against the background for readability.
 *
 * @param bgColor - The background color in hexadecimal format (e.g., '#FFFFFF').
 * @returns Dialogue charcoal (black) or white, depending on the brightness of the background color.
 */
export const getTextColorForBackground = (bgColor: string): string => {
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 128 ? colors.dialogueCharcoal : colors.white
}

/*
  This is a helper function to maintain the current search bar support. As of PR: #2959
  Scribe now expects filters to be explicit and any additional terms are only used
  to perform a fuzzy search. The same function exists within CP but should be
  deprecated at some point
*/
export function generateQueryParamsFromSearchQuery(
  rawSearchText: string,
): Record<string, string | string[]> {
  const terms = rawSearchText.trim().split(/\s+/).filter(Boolean)
  const result: Record<string, string | string[]> = {}
  const searchTerms = []

  // Parse out explicit terms that might exist in the search text
  for (const term of terms) {
    if (term.includes(':')) {
      const [key, value] = term.split(':')
      result[key] = value
    } else {
      searchTerms.push(term)
    }
  }

  // Place the remaining terms back in the search property
  if (searchTerms.length > 0) {
    result.search = searchTerms.join(' ')
  }

  return result
}
